import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { QUESTIONS } from "@/lib/questions";

export default function Questionnaire({ onDone, onBack }) {
  const [i, setI] = useState(0);
  const [answers, setAnswers] = useState({});
  const q = QUESTIONS[i];
  const progress = ((i + 1) / QUESTIONS.length) * 100;

  const setSingle = (val) => setAnswers((a) => ({ ...a, [q.id]: val }));
  const toggleMulti = (val) =>
    setAnswers((a) => {
      let cur = Array.isArray(a[q.id]) ? [...a[q.id]] : [];
      const exclusive = ["nessuno", "non_saprei"];
      if (exclusive.includes(val)) {
        cur = cur.includes(val) ? [] : [val];
      } else {
        cur = cur.filter((x) => !exclusive.includes(x));
        cur = cur.includes(val) ? cur.filter((x) => x !== val) : [...cur, val];
      }
      return { ...a, [q.id]: cur };
    });

  const answered = (() => {
    const v = answers[q.id];
    if (q.optional) return true;
    if (q.type === "multi") return Array.isArray(v) && v.length > 0;
    if (q.type === "text") return true;
    return v !== undefined && v !== null && v !== "";
  })();

  const next = () => {
    if (i + 1 < QUESTIONS.length) setI(i + 1);
    else onDone(answers);
  };
  const prev = () => (i === 0 ? onBack() : setI(i - 1));

  return (
    <div className="min-h-[100dvh] bg-[var(--fs-page)] flex flex-col">
      <div className="fs-container w-full pt-6">
        <div className="flex items-center gap-3">
          <button onClick={prev} className="text-[var(--fs-ink-2)] fs-focus rounded p-1" data-testid="questionnaire-back-button" aria-label="Indietro">
            <ArrowLeft size={18} />
          </button>
          <div className="flex-1 h-1.5 rounded-full bg-slate-200 overflow-hidden">
            <motion.div className="h-full bg-[var(--fs-blue)]" animate={{ width: `${progress}%` }} transition={{ duration: 0.3 }} />
          </div>
          <span className="text-xs text-[var(--fs-muted)] tabular-nums" data-testid="questionnaire-progress">{i + 1}/{QUESTIONS.length}</span>
        </div>
      </div>

      <div className="flex-1 fs-container w-full flex flex-col pt-8 pb-6">
        <AnimatePresence mode="wait">
          <motion.div
            key={q.id}
            initial={{ opacity: 0, x: 16 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -16 }}
            transition={{ duration: 0.25 }}
            className="flex-1"
          >
            <h2 className="text-xl sm:text-2xl font-bold leading-snug" data-testid="questionnaire-title">{q.title}</h2>
            {q.subtitle && <p className="text-sm text-[var(--fs-muted)] mt-2">{q.subtitle}</p>}

            <div className="mt-6 space-y-2.5">
              {q.type === "text" ? (
                <textarea
                  maxLength={q.maxLength}
                  value={answers[q.id] || ""}
                  onChange={(e) => setSingle(e.target.value)}
                  placeholder="Scrivi qui (facoltativo)…"
                  rows={4}
                  className="w-full p-4 rounded-2xl border border-[var(--fs-border)] bg-white text-[var(--fs-ink)] resize-none fs-focus"
                  data-testid="questionnaire-textarea"
                />
              ) : (
                q.options.map((opt) => {
                  const selected =
                    q.type === "multi"
                      ? Array.isArray(answers[q.id]) && answers[q.id].includes(opt.value)
                      : answers[q.id] === opt.value;
                  return (
                    <button
                      key={opt.value}
                      onClick={() => (q.type === "multi" ? toggleMulti(opt.value) : setSingle(opt.value))}
                      className={`w-full text-left p-4 rounded-2xl border flex items-center justify-between gap-3 transition-all fs-focus ${
                        selected ? "border-[var(--fs-blue)] bg-[var(--fs-tint)] shadow-sm" : "border-[var(--fs-border)] bg-white hover:border-[#BFDBFE]"
                      }`}
                      data-testid={`option-${q.id}-${opt.value}`}
                    >
                      <span className={`text-sm ${selected ? "text-[var(--fs-deep)] font-medium" : "text-[var(--fs-ink)]"}`}>{opt.label}</span>
                      <span className={`w-5 h-5 rounded-full border grid place-items-center shrink-0 ${selected ? "bg-[var(--fs-blue)] border-[var(--fs-blue)]" : "border-slate-300"}`}>
                        {selected && <Check size={13} className="text-white" strokeWidth={3} />}
                      </span>
                    </button>
                  );
                })
              )}
            </div>
          </motion.div>
        </AnimatePresence>

        <button onClick={next} disabled={!answered} className="fs-btn-primary w-full py-4 mt-6 flex items-center justify-center gap-2 fs-focus" data-testid="questionnaire-next-button">
          {i + 1 < QUESTIONS.length ? "Continua" : "Vedi il mio Skin Profile"} <ArrowRight size={18} />
        </button>
      </div>
    </div>
  );
}
